// eslint-disable-next-line eag/no-external-date-time-format-functions -- Special format because of Datagrid
import {formatRFC3339} from 'date-fns';
import {
  Chips,
  DateTimePicker,
  NumberInput,
  Separator,
  useTranslationContext,
} from 'platform/components';
import {HStack, VStack} from 'platform/foundation-tw';

// eslint-disable-next-line no-restricted-imports -- Intended
import {intersection, pathOr} from 'ramda';
import {isString} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

import {quickFiltersConst} from '../../constants/quickFilters';
import {useFilterOnChipsChange} from '../../hooks/useFilterOnChipsChange';
import type {EagFilterProps} from '../../types/EagFilterProps';
import {parseDateFilter} from '../../utils/parseDateFilter';

type DateTimeRangeModel = {from: string | null; to: string | null};
type RelativeInputModel = {relativeInputValue: number; relativeInputKey: string};

export type DateTimeRangeFilterProps = EagFilterProps & {
  min: string | null;
  max: string | null;
  relativeDates: Array<{value: string; label: string}>;
  relativeInputs: Array<{value: string; label: string}>;
  quickFilters?: Array<{value: string; label: string}>;
};

const FILTER_VALUE_KEY = 'value';

export function DateTimeRangeFilter(props: DateTimeRangeFilterProps) {
  const t = useTranslationContext();
  const minDate = parseDateFilter(props.min);
  const maxDate = parseDateFilter(props.max);

  const model = props.value[FILTER_VALUE_KEY] as
    | DateTimeRangeModel
    | RelativeInputModel
    | string
    | null;

  const onModelChange = (newModel: DateTimeRangeModel | RelativeInputModel | string | null) => {
    props.onChange({[FILTER_VALUE_KEY]: newModel});
  };

  const {onChipsChange} = useFilterOnChipsChange({
    setFilterValue: onModelChange,
    defaultValue: null,
  });

  const fromPickerValue = pathOr(null, ['from'], model);
  const toPickerValue = pathOr(null, ['to'], model);
  const inputValue = pathOr(0, ['relativeInputValue'], model);
  const chipsValue = isString(model) ? [model] : [];

  const onDateFromChange = (value: Date | null) => {
    onModelChange({
      from: value ? formatRFC3339(value) : null,
      to: pathOr(null, ['to'], model),
    });
  };

  const onDateToChange = (value: Date | null) => {
    onModelChange({
      to: value ? formatRFC3339(value) : null,
      from: pathOr(null, ['from'], model),
    });
  };

  const onInputChange = (relativeInputValue: number, relativeInputKey: string) => {
    const newModel =
      isNaN(relativeInputValue) || relativeInputValue === 0
        ? null
        : {relativeInputValue, relativeInputKey};

    onModelChange(newModel);
  };

  const isQuickFilterValue = intersection([model], quickFiltersConst) as string[];

  return (
    <VStack>
      {props.quickFilters && props.quickFilters.length > 0 && (
        <>
          <HStack>
            <Chips
              isDisabled={props.isDisabled}
              value={isQuickFilterValue}
              options={props.quickFilters}
              onChange={onChipsChange}
              isMultiple={false}
              data-testid={suffixTestId('date-time-range-filter-quickFilters', props)}
              isDeselectable
            />
          </HStack>
          <Separator />
        </>
      )}
      {props.relativeDates && props.relativeDates.length > 0 && (
        <>
          <Chips
            isDisabled={props.isDisabled}
            value={chipsValue}
            options={props.relativeDates}
            onChange={onChipsChange}
            isMultiple={false}
            data-testid={suffixTestId('date-time-range-filter-relativeDate', props)}
            isDeselectable
          />
          <Separator />
        </>
      )}
      <VStack spacing={2}>
        <DateTimePicker
          label={t('page.datagrid.filter.dateTimeLabelFrom')}
          minDate={minDate}
          maxDate={maxDate}
          isDisabled={props.isDisabled}
          value={fromPickerValue}
          onChange={onDateFromChange}
          data-testid={suffixTestId('date-time-range-filter-from', props)}
          isRelativeDatesHidden
        />
        <DateTimePicker
          label={t('page.datagrid.filter.dateTimeLabelTo')}
          minDate={minDate}
          maxDate={maxDate}
          isDisabled={props.isDisabled}
          value={toPickerValue}
          onChange={onDateToChange}
          data-testid={suffixTestId('date-time-range-filter-to', props)}
          isRelativeDatesHidden
        />
      </VStack>
      {props.relativeInputs.map((relativeInput) => (
        <NumberInput
          key={relativeInput.value}
          label={relativeInput.label}
          isDisabled={props.isDisabled}
          onChange={(value) => onInputChange(value ?? 0, relativeInput.value)}
          value={inputValue}
          data-testid={suffixTestId(
            `date-time-range-filter-relativeInput-${relativeInput.label}`,
            props
          )}
        />
      ))}
    </VStack>
  );
}
